import { ISplitActionHandler, SplitActionResult } from "./base";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
} from "../../../../../components/DeliveryOptions/types";
import { extractApiErrorMessage } from "../../../../../components/DeliveryOptions/utils/apiErrorUtils";
import services from "../../../services";

/**
 * Handles the cancellation flow for estimate splits.
 *
 * For each cancel selection, calls the split-level cancel API.
 * All splits are cancelled in parallel via Promise.allSettled.
 */
export class SplitCancelHandler implements ISplitActionHandler {
  async execute(
    estimateNo: string,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<SplitActionResult[]> {
    const cancelSelections = selections.filter(
      (s) => s.customerAction === "cancel",
    );

    const outcomes = await Promise.allSettled(
      cancelSelections.map((selection) =>
        this.processCancelWorkflow(estimateNo, selection, splitData),
      ),
    );

    return outcomes.map((outcome) => {
      if (outcome.status === "fulfilled") {
        return outcome.value;
      }
      return {
        supplierId: null,
        success: false,
        error: String(outcome.reason),
      };
    });
  }

  private async processCancelWorkflow(
    estimateNo: string,
    selection: NormalizedDeliverySelection,
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<SplitActionResult> {
    const supplierSplit = splitData.find(
      (s) => s.splitId === selection.splitId,
    );
    const supplierDisplayName =
      supplierSplit?.supplierName || "remaining items";

    try {
      await services.cancelEstimateSplit(estimateNo, selection.splitId);

      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: true,
      };
    } catch (error: unknown) {
      const errorMessage = extractApiErrorMessage(
        error,
        `Failed to cancel split for ${supplierDisplayName}`,
      );
      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: false,
        error: errorMessage,
      };
    }
  }
}
